import { toast } from "sonner";

/**
 * CourseLookupUI - Course lookup interface with multi-nine selection support
 * Handles 27-hole courses and multi-course facilities
 */
const CourseLookupUI = ({
  courseLookupStep,
  setCourseLookupStep,
  pendingCourseName,
  setPendingCourseName,
  pendingCourseLocation,
  setPendingCourseLocation,
  foundCourse,
  setFoundCourse,
  firstNineSelection,
  setFirstNineSelection,
  secondNineSelection,
  setSecondNineSelection,
  handleCourseLookup,
  handleNineSelection,
  confirmFoundCourse,
  speakText,
  setShowNewCourse,
  setEntryMode
}) => {
  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 space-y-4" data-testid="course-lookup-ui">
      {/* Logo Header */}
      <div className="text-center">
        <img 
          src="/images/par-seeker-logo.png" 
          alt="Par Seeker" 
          className="w-16 h-16 mx-auto object-contain"
        />
        <h4 className="font-bold text-green-900 mt-2">Find Your Course</h4>
      </div>

      {/* Idle State - Show input fields */}
      {(courseLookupStep === 'idle' || courseLookupStep === 'searching') && (
        <>
          {/* Course Name Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-green-800">Course Name</label>
            <input
              type="text"
              value={pendingCourseName}
              onChange={(e) => setPendingCourseName(e.target.value)}
              placeholder="e.g., Pebble Beach, Augusta National"
              className="w-full px-4 py-3 rounded-xl border-2 border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 text-base"
            />
          </div>

          {/* Location Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-green-800">City, County or State</label>
            <input
              type="text"
              value={pendingCourseLocation}
              onChange={(e) => setPendingCourseLocation(e.target.value)}
              placeholder="e.g., California, Atlanta GA"
              className="w-full px-4 py-3 rounded-xl border-2 border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 text-base"
            />
          </div>

          {/* Search Button */}
          <button
            onClick={() => handleCourseLookup(pendingCourseName, pendingCourseLocation)}
            disabled={!pendingCourseName || courseLookupStep === 'searching'}
            className="w-full py-3 bg-green-700 text-white rounded-xl font-bold hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {courseLookupStep === 'searching' ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Looking it up...
              </>
            ) : (
              <>
                🔍 Find My Course
              </>
            )}
          </button>
        </>
      )}

      {/* Searching State */}
      {courseLookupStep === 'searching' && (
        <div className="text-center py-4">
          <div className="w-10 h-10 border-4 border-green-200 border-t-green-700 rounded-full animate-spin mx-auto" />
          <p className="text-green-700 font-medium mt-3">Looking up your course...</p>
        </div>
      )}

      {/* Found Course Confirmation */}
      {foundCourse && courseLookupStep === 'confirm' && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 space-y-3">
          <div className="text-center">
            <span className="text-2xl">✅</span>
            <h5 className="font-bold text-green-800 mt-1">{foundCourse.name}</h5>
            <p className="text-sm text-green-700">{foundCourse.location}</p>
            <p className="text-lg font-bold text-green-800 mt-2">Par {foundCourse.par}</p>
            {foundCourse.confidence && (
              <p className="text-xs text-green-600 mt-1">
                Confidence: {foundCourse.confidence}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setFoundCourse(null);
                setCourseLookupStep('idle');
              }}
              className="flex-1 py-2 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg"
            >
              Not Right
            </button>
            <button
              onClick={confirmFoundCourse}
              className="flex-1 py-2 text-sm font-bold text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              That's It!
            </button>
          </div>
        </div>
      )}

      {/* Multi-Course/Multi-Nine Facility - Select Which to Play */}
      {foundCourse && courseLookupStep === 'select_nines' && foundCourse.nines && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 space-y-4" data-testid="select-nines-ui">
          <div className="text-center">
            <span className="text-2xl">⛳</span>
            <h5 className="font-bold text-green-800 mt-1">{foundCourse.name}</h5>
            <p className="text-sm text-green-600">{foundCourse.nines.length} Options Available</p>
          </div>
          
          <p className="text-sm text-green-700 text-center">
            {foundCourse.nines[0]?.hole_pars?.length === 9 
              ? "Which two nines are you playing today?"
              : "Select your course for today:"}
          </p>
          
          {/* For 9-hole nines (27-hole facility) - Select two */}
          {foundCourse.nines[0]?.hole_pars?.length === 9 ? (
            <>
              {/* First Nine Selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-green-800">First Nine (Holes 1-9):</label>
                <div className="grid grid-cols-1 gap-2">
                  {foundCourse.nines.map((nine, idx) => (
                    <button
                      key={`first-${idx}`}
                      onClick={() => {
                        setFirstNineSelection(idx);
                        if (secondNineSelection === idx) setSecondNineSelection(null);
                      }}
                      disabled={secondNineSelection === idx}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        firstNineSelection === idx
                          ? 'border-green-600 bg-green-100 ring-2 ring-green-400'
                          : secondNineSelection === idx
                            ? 'border-stone-200 bg-stone-100 opacity-50 cursor-not-allowed'
                            : 'border-stone-300 bg-white hover:border-green-400'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-green-800">{nine.name}</span>
                        <span className="text-sm font-bold text-green-600">Par {nine.par}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Second Nine Selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-green-800">Second Nine (Holes 10-18):</label>
                <div className="grid grid-cols-1 gap-2">
                  {foundCourse.nines.map((nine, idx) => (
                    <button
                      key={`second-${idx}`}
                      onClick={() => setSecondNineSelection(idx)}
                      disabled={firstNineSelection === idx}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        secondNineSelection === idx
                          ? 'border-green-600 bg-green-100 ring-2 ring-green-400'
                          : firstNineSelection === idx
                            ? 'border-stone-200 bg-stone-100 opacity-50 cursor-not-allowed'
                            : 'border-stone-300 bg-white hover:border-green-400'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-green-800">{nine.name}</span>
                        <span className="text-sm font-bold text-green-600">Par {nine.par}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Confirm Selection for 27-hole */}
              {firstNineSelection !== null && secondNineSelection !== null && (
                <div className="pt-2 border-t border-green-200">
                  <p className="text-sm text-green-700 text-center mb-3">
                    Playing: <strong>{foundCourse.nines[firstNineSelection].name}</strong> → <strong>{foundCourse.nines[secondNineSelection].name}</strong>
                    <br />
                    <span className="text-green-600">
                      Total Par: {foundCourse.nines[firstNineSelection].par + foundCourse.nines[secondNineSelection].par}
                    </span>
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setFirstNineSelection(null);
                        setSecondNineSelection(null);
                        setFoundCourse(null);
                        setCourseLookupStep('idle');
                      }}
                      className="flex-1 py-2 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleNineSelection(firstNineSelection, secondNineSelection)}
                      className="flex-1 py-2 text-sm font-bold text-white bg-green-600 rounded-lg hover:bg-green-700"
                    >
                      Confirm Selection
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* For 18-hole courses (multi-course facility) - Select one course */
            <div className="space-y-2">
              <label className="text-xs font-bold text-green-800">Select Course:</label>
              <div className="grid grid-cols-1 gap-2">
                {foundCourse.nines.map((course, idx) => (
                  <button
                    key={`course-${idx}`}
                    onClick={async () => {
                      // Set this course directly
                      setFoundCourse({
                        ...foundCourse,
                        name: `${foundCourse.name} - ${course.name}`,
                        hole_pars: course.hole_pars,
                        par: course.par,
                        selectedNines: { course: course.name }
                      });
                      setCourseLookupStep('confirm');
                      const confirmText = `Playing ${course.name}. Par ${course.par}. Sound right?`;
                      await speakText(confirmText);
                    }}
                    className="p-3 rounded-lg border-2 text-left transition-all border-stone-300 bg-white hover:border-green-400 hover:bg-green-50"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-green-800">{course.name}</span>
                      <span className="text-sm font-bold text-green-600">Par {course.par} • {course.hole_pars?.length || 18} holes</span>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setFoundCourse(null);
                  setCourseLookupStep('idle');
                }}
                className="w-full py-2 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg mt-2"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Not Found - Offer Alternatives */}
      {courseLookupStep === 'not_found' && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 text-center space-y-3">
          <span className="text-2xl">🤔</span>
          <p className="text-sm text-amber-800">
            Couldn't find that course. Try a different spelling, or:
          </p>
          <button
            onClick={() => {
              setCourseLookupStep('idle');
              setEntryMode('scan');
              toast.info("Snap a photo of your scorecard to set up the course");
            }}
            className="w-full py-2 text-sm font-bold text-amber-800 bg-amber-200 rounded-lg hover:bg-amber-300"
          >
            📸 Scan Scorecard Instead
          </button>
        </div>
      )}

      {/* Skip Option */}
      <button
        onClick={() => setShowNewCourse(true)}
        className="w-full py-2 text-sm text-green-700 hover:text-green-900"
      >
        Or add course manually →
      </button>
    </div>
  );
};

export default CourseLookupUI;
